import React, { createContext, useContext, useEffect, useState } from 'react';
import { io } from 'socket.io-client';
import { message } from 'antd';
import { readAuthToken } from '../utils/authToken';
import { useAuthStore } from '../store/authenticated/authenticated.state';
import { Task } from '../interfaces/Task';

interface TaskContextType {
  tasks: Task[];
  setTasks: (tasks: Task[]) => void;
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;
  selectedTask: Task | null;
  setSelectedTask: (task: Task | null) => void;
  clearCompletedTasks: () => Promise<void>;
  socket: ReturnType<typeof io> | null;
  isAdmin: boolean;
  showMyTasksOnly: boolean;
  setShowMyTasksOnly: (show: boolean) => void;
  filteredTasks: Task[];
}

const TaskContext = createContext<TaskContextType | undefined>(undefined);

export const TaskProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [socket, setSocket] = useState<ReturnType<typeof io> | null>(null);
  const [showMyTasksOnly, setShowMyTasksOnly] = useState(false);
  const token = readAuthToken();
  const { user } = useAuthStore();

  const isAdmin = user?.roles?.includes('Super Admin') || user?.roles?.includes('Admin') || false;

  // Filter tasks based on showMyTasksOnly setting
  const filteredTasks =
    showMyTasksOnly && user?.id ? tasks.filter(task => task.userId === user.id) : tasks;

  useEffect(() => {
    if (!token) {
      console.warn('No auth token available for socket connection');
      return;
    }

    // Remove '/api' suffix if it exists
    const baseUrl = process.env.REACT_APP_API?.replace(/\/api\/?$/, '');

    const newSocket = io(baseUrl, {
      auth: { token: `Bearer ${token}` },
      transports: ['websocket'],
      forceNew: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20000,
      autoConnect: false,
      path: '/socket.io/',
      addTrailingSlash: false,
    });

    // Add error handlers before connecting
    newSocket.on('connect_error', error => {
      if (error.message) {
        message.error(`Failed to connect to task system: ${error.message}`);
      }
    });

    newSocket.on('error', error => {
      message.error(`Socket error: ${error.message}`);
    });

    newSocket.on('connect', () => {
      // console.log('Socket connected');
    });

    newSocket.on('disconnect', reason => {
      if (reason === 'io server disconnect') {
        newSocket.connect();
      }
    });

    newSocket.on('taskUpdate', (task: Task) => {
      setTasks(prev => {
        const index = prev.findIndex(t => t.id === task.id);
        if (index >= 0) {
          const newTasks = [...prev];
          newTasks[index] = task;
          return newTasks;
        }
        return [task, ...prev];
      });
    });

    newSocket.on('taskComplete', (task: Task) => {
      setTasks(prev => {
        const index = prev.findIndex(t => t.id === task.id);
        if (index >= 0) {
          const newTasks = [...prev];
          newTasks[index] = task;
          return newTasks;
        }
        return [task, ...prev];
      });
      if (task.userId === user?.id) {
        message.success(`Task ${task.type} completed successfully`);
      }
    });

    newSocket.on('taskCancelled', (task: Task) => {
      setTasks(prev => {
        const index = prev.findIndex(t => t.id === task.id);
        if (index >= 0) {
          const newTasks = [...prev];
          newTasks[index] = task;
          return newTasks;
        }
        return [task, ...prev];
      });
      if (task.userId === user?.id) {
        message.warn(`Task ${task.type} cancelled`);
      }
    });

    newSocket.on('taskError', (task: Task) => {
      setTasks(prev => {
        const index = prev.findIndex(t => t.id === task.id);
        if (index >= 0) {
          const newTasks = [...prev];
          newTasks[index] = task;
          return newTasks;
        }
        return [task, ...prev];
      });
      message.error(`Task ${task.type} failed: ${task.error}`);
    });

    newSocket.on('allTasks', (tasks: Task[]) => {
      setTasks(tasks);
    });

    // Now connect
    newSocket.connect();
    setSocket(newSocket);

    // Fetch existing tasks
    fetchTasks();

    return () => {
      if (newSocket.connected) {
        newSocket.disconnect();
      }
    };
  }, [token]);

  const fetchTasks = async () => {
    if (!token) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API}tasks/user`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json();
      if (data.success) {
        setTasks(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
      message.error('Failed to load existing tasks');
    }
  };

  const clearCompletedTasks = async () => {
    try {
      if (!token) return;

      // Only keep active tasks in the backend
      const activeTasks = tasks.filter(
        task => task.status === 'pending' || task.status === 'running',
      );

      // Update backend
      await fetch(`${process.env.REACT_APP_API}tasks/clear-completed`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Update local state
      setTasks(activeTasks);
    } catch (error) {
      console.error('Failed to clear completed tasks:', error);
      message.error('Failed to clear completed tasks');
    }
  };

  return (
    <TaskContext.Provider
      value={{
        tasks,
        setTasks,
        isDrawerOpen,
        setIsDrawerOpen,
        selectedTask,
        setSelectedTask,
        clearCompletedTasks,
        socket,
        isAdmin,
        showMyTasksOnly,
        setShowMyTasksOnly,
        filteredTasks,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};

export const useTask = () => {
  const context = useContext(TaskContext);
  if (context === undefined) {
    throw new Error('useTask must be used within a TaskProvider');
  }
  return context;
};
